import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getApplication, submitApplicationPersonal, uploadApplicationDoc, submitApplication } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Check, AlertCircle, DoorOpen, Upload } from 'lucide-react';

export default function ApplyPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', home_address: '', business_name: '', business_type: '' });
  const [saving, setSaving] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [uploadedId, setUploadedId] = useState(false);
  const [uploadedProof, setUploadedProof] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setFormError(''); };

  useEffect(() => {
    if (!token) { setError('No application token provided'); setLoading(false); return; }
    getApplication(token)
      .then(d => {
        setData(d);
        if (d.has_personal_details) setStep(2);
        if (d.has_documents) { setUploadedId(true); setUploadedProof(true); }
        if (d.application_status === 'submitted' || d.onboarding_status === 'under_review') setSubmitted(true);
        setForm(f => ({ ...f, full_name: d.name || '', email: d.email || '', phone: d.phone || '' }));
      })
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired application link'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSavePersonal = async (e) => {
    if (e) e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) { setFormError('Full name and email are required'); return; }
    setSaving(true); setFormError('');
    try { await submitApplicationPersonal(token, form); setStep(2); }
    catch (err) { setFormError(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleUpload = async (file, docType, setUploaded) => {
    setSaving(true);
    try { await uploadApplicationDoc(token, file, docType); setUploaded(true); }
    catch (err) { setFormError('Upload failed. Please try again.'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    setSaving(true); setFormError('');
    try { await submitApplication(token); setSubmitted(true); }
    catch (err) { setFormError(err.response?.data?.detail || 'Please upload required documents to continue'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <Helmet><title>Studio Application | Go Freestyle Studios</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="px-6 py-6">
        <Link to="/" className="flex items-center"><img src="/logo.webp" alt="Go Freestyle Studios" className="h-[52px]" style={{filter:'contrast(1.15) brightness(0.95)'}} /></Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          {/* Error */}
          {error && (
            <div className="text-center" data-testid="apply-error">
              <div className="w-14 h-14 rounded-2xl bg-[#FDF2F2] flex items-center justify-center mx-auto mb-4"><AlertCircle className="w-7 h-7 text-[#B33A3A]" strokeWidth={1.5} /></div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">Application Issue</h1>
              <p className="text-sm text-[#5C5C55]">{error}</p>
            </div>
          )}

          {/* Submitted */}
          {submitted && !error && (
            <div className="text-center animate-fade-in" data-testid="apply-submitted">
              <div className="w-14 h-14 rounded-2xl bg-[#E8EDE5] flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-[#3A4035]" strokeWidth={1.5} /></div>
              <h1 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">Application Submitted</h1>
              <p className="text-sm text-[#5C5C55] mb-2">Thank you. Your application is being reviewed.</p>
              <p className="text-sm text-[#5C5C55]">We'll be in touch with next steps shortly.</p>
            </div>
          )}

          {/* Application form */}
          {data && !submitted && !error && (
            <div className="animate-fade-in" data-testid="apply-form">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-[#E8EDE5] flex items-center justify-center mx-auto mb-4"><DoorOpen className="w-7 h-7 text-[#3A4035]" strokeWidth={1.5} /></div>
                <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-2">Studio Application</h1>
                {data.studio_name && <p className="text-sm text-[#3A4035] font-medium">{data.studio_name}{data.site_name ? ` at ${data.site_name}` : ''}</p>}
                <p className="text-sm text-[#5C5C55] mt-1">Step {step} of 2 — {step === 1 ? 'Your Details' : 'Documents'}</p>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#3A4035]' : 'text-[#8A8B85]'}`}>
                  <span className={`w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center ${step > 1 ? 'bg-[#E8EDE5] text-[#3A4035]' : step === 1 ? 'bg-[#3A4035] text-white' : 'bg-[#F2F0EB]'}`}>{step > 1 ? <Check className="w-3.5 h-3.5" strokeWidth={2}/> : '1'}</span>
                  <span className="text-xs font-medium">Details</span>
                </div>
                <div className="flex-1 h-px bg-[#E5E2DC]" />
                <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#3A4035]' : 'text-[#8A8B85]'}`}>
                  <span className={`w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center ${step === 2 ? 'bg-[#3A4035] text-white' : 'bg-[#F2F0EB]'}`}>2</span>
                  <span className="text-xs font-medium">Documents</span>
                </div>
              </div>

              {formError && (
                <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4] mb-4" data-testid="apply-form-error">
                  <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#B33A3A]">{formError}</p>
                </div>
              )}

              {/* Step 1 */}
              {step === 1 && (
                <form onSubmit={handleSavePersonal} className="space-y-4">
                  <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name <span className="text-[#B33A3A]">*</span></Label>
                    <Input value={form.full_name} onChange={e => set('full_name', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="apply-name" autoFocus /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email <span className="text-[#B33A3A]">*</span></Label>
                      <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="apply-email" /></div>
                    <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
                      <Input value={form.phone} onChange={e => set('phone', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="apply-phone" /></div>
                  </div>
                  <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Home Address</Label>
                    <Input value={form.home_address} onChange={e => set('home_address', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="apply-address" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Business Name</Label>
                      <Input value={form.business_name} onChange={e => set('business_name', e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="apply-biz-name" /></div>
                    <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Service Type</Label>
                      <Input value={form.business_type} onChange={e => set('business_type', e.target.value)} placeholder="e.g. Beauty, Massage" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-11" data-testid="apply-biz-type" /></div>
                  </div>
                  <Button type="submit" disabled={saving || !form.full_name.trim() || !form.email.trim()} className="w-full bg-[#3A4035] text-white rounded-full h-11 hover:bg-[#2B3027]" data-testid="apply-next">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Next: Documents
                  </Button>
                </form>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-1 block">Photo ID <span className="text-[#B33A3A]">*</span></Label>
                    <p className="text-[10px] text-[#8A8B85] mb-2">Please upload a valid photo ID (passport or driving licence)</p>
                    {uploadedId ? (
                      <div className="flex items-center gap-2 p-3 bg-[#E8EDE5] rounded-xl"><Check className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5}/><span className="text-xs font-medium text-[#3A4035]">{idFile?.name || 'Uploaded'}</span></div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input type="file" accept="image/*,.pdf" onChange={e => setIdFile(e.target.files[0])} className="text-xs flex-1" data-testid="apply-id-file" />
                        <Button onClick={() => handleUpload(idFile, 'photo_id', setUploadedId)} disabled={!idFile || saving} size="sm" className="bg-[#3A4035] text-white rounded-full px-4 hover:bg-[#2B3027] text-xs" data-testid="apply-upload-id">
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-1 block">Proof of Address <span className="text-[#B33A3A]">*</span></Label>
                    <p className="text-[10px] text-[#8A8B85] mb-2">Please upload a utility bill or bank statement dated within the last 3 months</p>
                    {uploadedProof ? (
                      <div className="flex items-center gap-2 p-3 bg-[#E8EDE5] rounded-xl"><Check className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5}/><span className="text-xs font-medium text-[#3A4035]">{proofFile?.name || 'Uploaded'}</span></div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <input type="file" accept="image/*,.pdf" onChange={e => setProofFile(e.target.files[0])} className="text-xs flex-1" data-testid="apply-proof-file" />
                        <Button onClick={() => handleUpload(proofFile, 'proof_of_address', setUploadedProof)} disabled={!proofFile || saving} size="sm" className="bg-[#3A4035] text-white rounded-full px-4 hover:bg-[#2B3027] text-xs" data-testid="apply-upload-proof">
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        </Button>
                      </div>
                    )}
                  </div>
                  {(!uploadedId || !uploadedProof) && (
                    <p className="text-xs text-[#8A7230] bg-[#FDFAF0] rounded-xl p-3 border border-[#EDE4C8]">Both documents are required before you can submit your application.</p>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-full h-11 border-[#E5E2DC]">Back</Button>
                    <Button onClick={handleSubmit} disabled={saving || !uploadedId || !uploadedProof} className="flex-1 bg-[#3A4035] text-white rounded-full h-11 hover:bg-[#2B3027]" data-testid="apply-submit">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" strokeWidth={1.5}/>}Submit Application
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
